import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, Printer, DownloadSimple, Wrench } from "@phosphor-icons/react";
import { fmtDate } from "../lib/date";

const RANGES = [
  { v: "3m", l: "Last 3 months", months: 3 },
  { v: "6m", l: "Last 6 months", months: 6 },
  { v: "12m", l: "Last 12 months", months: 12 },
  { v: "all", l: "Full history", months: 0 },
  { v: "custom", l: "Custom dates", months: -1 },
];

function isoMonthsAgo(m) {
  const d = new Date();
  d.setMonth(d.getMonth() - m);
  return d.toISOString().slice(0, 10);
}

export default function ServiceHistoryPage() {
  const { hasPerm } = useAuth();
  const canVoidView = hasPerm("administrator", "auditor", "supervisor");
  const [params, setParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [assetId, setAssetId] = useState(params.get("asset") || "");
  const [asset, setAsset] = useState(null);
  const [q, setQ] = useState("");
  const [workTypes, setWorkTypes] = useState([]);
  const [range, setRange] = useState("12m");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [workTypeId, setWorkTypeId] = useState("");
  const [includeVoided, setIncludeVoided] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/config/work-types").then((r) => setWorkTypes(r.data)).catch(() => {});
  }, []);

  const dates = useMemo(() => {
    if (range === "all") return {};
    if (range === "custom") return { from: custom.from, to: custom.to };
    const m = RANGES.find((r) => r.v === range)?.months || 12;
    return { from: isoMonthsAgo(m), to: "" };
  }, [range, custom]);

  const load = useCallback(() => {
    if (!assetId) { setRows([]); setAsset(null); return; }
    api.get(`/assets/${assetId}`).then((r) => setAsset(r.data)).catch(() => {});
    const p = new URLSearchParams();
    if (dates.from) p.set("date_from", dates.from);
    if (dates.to) p.set("date_to", dates.to);
    if (workTypeId) p.set("work_type_id", workTypeId);
    if (includeVoided && canVoidView) p.set("include_voided", "true");
    api.get(`/assets/${assetId}/maintenance-history?${p.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [assetId, dates, workTypeId, includeVoided, canVoidView]);
  useEffect(() => { load(); }, [load]);

  const pickAsset = (id) => { setAssetId(id); setParams(id ? { asset: id } : {}); };
  const filteredAssets = assets.filter((a) => !q || `${a.asset_number} ${a.name} ${a.registration || ""}`.toLowerCase().includes(q.toLowerCase()));
  const money = (v) => (v == null ? "" : `£${Number(v).toFixed(2)}`);

  const exportCsv = async () => {
    if (!assetId) return;
    try {
      const p = new URLSearchParams({ asset_id: assetId });
      if (dates.from) p.set("date_from", dates.from);
      if (dates.to) p.set("date_to", dates.to);
      if (workTypeId) p.set("work_type_id", workTypeId);
      if (includeVoided && canVoidView) p.set("include_voided", "true");
      const resp = await api.get(`/reports/asset-service-history.csv?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = "service-history.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Service History</h1>
        {assetId && (
          <div className="flex gap-2">
            <Button data-testid="sh-print" variant="outline" onClick={() => window.print()} className="font-semibold"><Printer size={16} weight="bold" className="mr-1" />Print / PDF</Button>
            <Button data-testid="sh-export" variant="outline" onClick={exportCsv} className="font-semibold"><DownloadSimple size={16} weight="bold" className="mr-1" />CSV</Button>
          </div>
        )}
      </div>

      {/* Asset picker + filters */}
      <div className="bg-white border-2 border-slate-200 rounded-lg p-3 flex flex-col gap-2 print:hidden">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="sh-asset-search" placeholder="Search an asset by number, name or registration…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 pl-8" />
        </div>
        <select data-testid="sh-asset-select" value={assetId} onChange={(e) => pickAsset(e.target.value)} className="h-10 border-2 border-slate-300 rounded px-2 text-sm">
          <option value="">Select an asset…</option>
          {filteredAssets.map((a) => <option key={a.id} value={a.id}>{a.asset_number} · {a.name}{a.registration ? ` · ${a.registration}` : ""}</option>)}
        </select>
        {assetId && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <select data-testid="sh-range" value={range} onChange={(e) => setRange(e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
              {RANGES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
            {range === "custom" && <>
              <Input data-testid="sh-from" type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="h-9 text-sm" />
              <Input data-testid="sh-to" type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="h-9 text-sm" />
            </>}
            <select data-testid="sh-worktype" value={workTypeId} onChange={(e) => setWorkTypeId(e.target.value)} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
              <option value="">All work types</option>{workTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {canVoidView && <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><input type="checkbox" data-testid="sh-voided" checked={includeVoided} onChange={(e) => setIncludeVoided(e.target.checked)} />Include voided/reversed</label>}
          </div>
        )}
      </div>

      {/* Printable report */}
      {assetId && asset && (
        <div className="bg-white border-2 border-slate-200 rounded-lg p-5 print:border-0" data-testid="sh-report">
          <div className="border-b-2 border-slate-200 pb-3 mb-4">
            <h2 className="font-display text-2xl font-black text-slate-900">TMIC — Asset Service History</h2>
            <p className="font-mono text-sm text-slate-600 mt-1">
              {asset.asset_number} · {asset.name}
              {asset.registration ? ` · Reg ${asset.registration}` : ""}
              {asset.vin ? ` · VIN ${asset.vin}` : (asset.serial_number ? ` · Serial ${asset.serial_number}` : "")}
              {asset.model_text ? ` · ${asset.model_text}` : ""}
            </p>
            <p className="font-mono text-xs text-slate-500 mt-1">
              {asset.current_mileage != null ? `${asset.current_mileage} mi · ` : ""}{asset.current_hours != null ? `${asset.current_hours} h · ` : ""}
              Period: {fmtDate(dates.from) || "start"} → {fmtDate(dates.to) || "today"} · Generated {fmtDate(new Date())}
            </p>
          </div>
          {rows.length === 0 ? <p className="text-slate-400 text-sm" data-testid="sh-empty">No service records for this period.</p> : (
            <div className="flex flex-col gap-3" data-testid="sh-list">
              {rows.map((w) => (
                <div key={w.id} data-testid={`sh-row-${w.id}`} className={`border-2 rounded-lg p-3 break-inside-avoid ${w.voided ? "border-red-200 bg-red-50/40" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-body font-semibold text-slate-900 flex items-center gap-2"><Wrench size={15} weight="bold" className="text-slate-400" />{fmtDate(w.work_date) || "—"} — {w.work_type_name || "Work"}</span>
                    <span className="font-mono text-xs text-slate-500">{w.total_cost != null ? money(w.total_cost) : ""}{w.voided ? " · VOIDED/REVERSED" : ""}</span>
                  </div>
                  <p className="font-body text-sm text-slate-700 mt-1">{w.description}</p>
                  <p className="font-mono text-xs text-slate-500 mt-1">
                    {w.mileage != null ? `${w.mileage} mi · ` : ""}{w.hours != null ? `${w.hours} h · ` : ""}
                    {w.performed_by_name || w.technician || ""}{w.job_title ? ` · ${w.job_title}` : ""}
                    {w.rts_outcome ? ` · ${w.rts_outcome}${w.rts_by_name ? ` by ${w.rts_by_name}` : ""}${w.rts_date ? ` (${fmtDate(w.rts_date)})` : ""}` : ""}
                  </p>
                  {w.parts?.length > 0 && <div className="mt-1 text-xs font-mono text-slate-600">{w.parts.map((p, i) => <div key={i}>• {p.description} {p.part_number ? `(${p.part_number})` : ""} × {p.quantity}</div>)}</div>}
                  {w.notes && <p className="text-xs text-slate-500 mt-1">Notes: {w.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
